// Client side of "Sign in with Yarn" (server: routes/auth.ts). The session is
// an httpOnly cookie the browser sends on its own, so this module only (a)
// asks `/auth/me` who we are, (b) builds the login/logout calls, and (c) lets
// any API wrapper that sees a 401 `unauthenticated` flip the app into the
// sign-in screen (`requestSignIn` → `<SphinxAuthGate>` listener).
import { accessKeyHeaders } from "./sphinx-access-key";

export type SphinxAuthMode = "disabled" | "session" | "access-key";

export type SphinxUser = { sub: string; email: string; exp: number };

export type SphinxMe = { mode: SphinxAuthMode; user: SphinxUser | null };

/**
 * What the app should render for an identity.
 *
 * `access-key` is the service-account path — curl, CI, the runner — not a
 * browser sign-in: it satisfies the `/api/sphinx/*` gate but carries no
 * identity, so the sidebar has nobody to show and no way to sign out, while
 * every `/api/mcp/*` call (connectors, playground) still 401s because those do
 * not send the header. A browser holding one is stuck in a half-signed-in state
 * (David, 2026-08-22), so it is shown the sign-in screen with a way to drop the
 * stored key.
 */
export function gateDecisionFor(me: SphinxMe | null): "app" | "sign-in" | "stored-key" {
  if (!me) return "sign-in";
  if (me.mode === "session" && me.user) return "app";
  if (me.mode === "disabled") return "app";
  if (me.mode === "access-key") return "stored-key";
  return "sign-in";
}

/** Does a response mean "no sphinx session"? (the gate's 401 body) */
export async function isUnauthenticatedResponse(res: Response): Promise<boolean> {
  if (res.status !== 401) return false;
  try {
    const body = (await res.clone().json()) as { error?: unknown };
    return body?.error === "unauthenticated";
  } catch {
    return false;
  }
}

/** `null` = not signed in (the gate's 401); throws on other failures. */
export async function fetchSphinxMe(): Promise<SphinxMe | null> {
  const res = await fetch("/auth/me", { headers: accessKeyHeaders(), credentials: "same-origin" });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`/auth/me failed (${res.status})`);
  return (await res.json()) as SphinxMe;
}

export function signInUrl(next: string = window.location.pathname + window.location.search): string {
  return `/auth/login?next=${encodeURIComponent(next)}`;
}

export function goToSignIn(): void {
  window.location.href = signInUrl();
}

export async function signOut(): Promise<void> {
  await fetch("/auth/logout", { method: "POST", credentials: "same-origin" }).catch(() => {});
  window.location.href = "/";
}

type Listener = () => void;
const listeners = new Set<Listener>();

/** The gate subscribes; returns an unsubscribe. */
export function onSignInRequired(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Called by API wrappers on a 401 `unauthenticated`. */
export function requestSignIn(): void {
  for (const fn of listeners) fn();
}
